import React, { useEffect, useState } from 'react';
import { accent } from '../helpers';

/* ─────────────── Le château de cartes — slide 27, la forge ───────────────
 * LA SLIDE SE JOUE EN QUATRE TEMPS, un par appui (voir le conducteur) :
 *   0. le château se construit, carte par carte — « par ajouts successifs,
 *      comme un château de cartes » ;
 *   1. « quand il faut toucher aux fondations… » — il s'effondre ; quand les
 *      cartes s'immobilisent, « plus de 3000 jours » se pose au-dessus ;
 *   2. « …travailler autrement » — les cartes se relèvent et reprennent leur
 *      place ; à la dernière, « 1000 jours » arrive et 3000 recule ;
 *   3. « …en décembre 2025 » — la note.
 * Les chiffres tombent AVEC le mot : c'est pour ça que le conducteur a été
 * réordonné le 23/09 (le 3000 était dit avant l'effondrement, le 1000 cinq
 * secondes après la reconstruction).
 *
 * POURQUOI L'EFFONDREMENT, alors que la slide 28 fait déjà tomber la barre des
 * 15 jours : ici la chute n'est pas la fin de l'image. Le château se relève,
 * et c'est l'histoire de la forge — tout refaire, autrement. Il ne tremble
 * qu'après la première construction, pas après la seconde : le château
 * reconstruit est celui qui tient.
 *
 * UN VRAI OBJET, PAS UN PICTOGRAMME. La première version était quinze traits
 * violets. Le niveau attendu est celui du verre brisé de l'autre conférence
 * (components/glass.jsx) : de vraies cartes à jouer en 3D CSS, vues de trois
 * quarts, éclairées d'un côté, posées sur un tapis avec leur ombre.
 *
 * LA MÉCANIQUE. Chaque carte a trois poses complètes — en l'air, debout, à
 * terre — écrites avec LA MÊME liste de fonctions CSS (translate3d, rotateZ,
 * rotateY, rotateX). Le navigateur interpole alors chaque angle séparément :
 * passer de « debout » à « à terre » fait basculer et pivoter la carte en
 * tombant, sans une ligne de physique. Les poses à terre viennent d'un tirage
 * FIXE (graine) : la chute est la même à chaque répétition.
 *
 * ⚠️ PAS DE TEXTE MÉTIER SUR LES CARTES (CVS, 1986…) : ça se dit.
 *
 * ÉCHELLE : toutes les cotes sont en `em`, et la taille de police du conteneur
 * suit la hauteur réservée — mêmes proportions du portable au vidéoprojecteur.
 */

// Une carte de 63 × 88 mm, au format réel.
const W = 6.3;                 // largeur (em) — part en profondeur une fois debout
const H = 8.8;                 // hauteur (em)
const T = 0.12;                // épaisseur apparente d'un plateau
const INCLINAISON = 17;        // degrés depuis la verticale
const RAD = (INCLINAISON * Math.PI) / 180;
const DEMI_BASE = H * Math.sin(RAD);       // pied d'une carte ↔ axe du chevron
const HAUT_CHEVRON = H * Math.cos(RAD);
const PAS = 7.9;               // entre deux sommets d'un même étage (< H : le plateau porte)

/* Tirage pseudo-aléatoire à graine fixe : la même chute à chaque fois. */
const hasard = (i, k) => {
    const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453;
    return s - Math.floor(s);
};

/* Les faces visibles alternent pour que le jeu ait l'air d'un vrai jeu battu. */
const FACES = [
    ['A', '♠'], ['K', '♥'], ['7', '♣'], ['Q', '♦'], ['J', '♠'], ['9', '♥'],
    ['3', '♦'], ['10', '♣'], ['A', '♥'], ['5', '♠'], ['K', '♣'], ['2', '♦']
];

/* Ordre du tableau = ordre de pose : étage par étage, de gauche à droite, les
   deux cartes d'un chevron puis les plateaux de l'étage. */
const CARTES = (() => {
    const cartes = [];
    let f = 0;
    [3, 2, 1].forEach((n, etage) => {
        const sol = -etage * (HAUT_CHEVRON + T);
        const sommets = Array.from({ length: n }, (_, j) => (j - (n - 1) / 2) * PAS);
        sommets.forEach((x) => {
            [-1, 1].forEach((cote) => {
                const angle = -cote * INCLINAISON;
                const a = (angle * Math.PI) / 180;
                const pied = x + cote * DEMI_BASE;
                cartes.push({
                    type: 'penchee', etage, angle, face: FACES[f++ % FACES.length],
                    w: W, h: H,
                    // Le centre de la carte, une fois inclinée autour de son pied.
                    debout: { x: pied + (H / 2) * Math.sin(a), y: sol - (H / 2) * Math.cos(a), z: 0, rz: angle, ry: 90, rx: 0 }
                });
            });
        });
        sommets.slice(0, -1).forEach((x, j) => {
            cartes.push({
                type: 'plateau', etage, w: H, h: W,
                debout: { x: (x + sommets[j + 1]) / 2, y: sol - HAUT_CHEVRON - T, z: 0, rz: 0, ry: 0, rx: 90 }
            });
        });
    });

    // Les poses à terre : chaque carte glisse vers l'extérieur de là où elle
    // était, pivote sur le tapis, et se couche d'un côté ou de l'autre.
    return cartes.map((c, i) => {
        const d = c.debout;
        const aTerre = {
            x: d.x * 1.35 + (hasard(i, 1) - 0.5) * 9,
            y: -0.03 - i * 0.02,                       // empilées, sans se traverser
            z: (hasard(i, 2) - 0.5) * 11,
            rz: (hasard(i, 3) - 0.5) * 8,              // quelques-unes restent en appui
            ry: d.ry + (hasard(i, 4) - 0.5) * 200,
            rx: c.type === 'plateau' ? 90 : (hasard(i, 5) > 0.5 ? 90 : -90)
        };
        const enLAir = { ...d, y: d.y - 4.5, rz: d.rz + (i % 2 ? 6 : -6) };
        return { ...c, aTerre, enLAir };
    });
})();

/* ─── Le calendrier ─── */
const DEPART = 350;            // ms : laisse le kicker s'afficher d'abord
const PAS_POSE = 150;          // ms entre deux cartes — ~2,2 s pour les 15
const DUREE_POSE = 520;
const FIN_POSE = DEPART + (CARTES.length - 1) * PAS_POSE + DUREE_POSE;

// L'effondrement part d'UNE carte du bas — « toucher aux fondations » — et
// entraîne le reste ; les étages hauts tombent de plus haut, donc plus tard
// et plus longtemps.
const CARTE_FONDATION = 3;
const chute = (c, i) => {
    const delai = i === CARTE_FONDATION ? 0 : 140 + c.etage * 70 + hasard(i, 6) * 110;
    const duree = 620 + c.etage * 130 + hasard(i, 7) * 160;
    return { delai, duree };
};
const FIN_CHUTE = Math.round(Math.max(...CARTES.map((c, i) => { const { delai, duree } = chute(c, i); return delai + duree; })));
// « …tout s'effondre. Conclusion de l'analyse : plus de » — environ 2,8 s
// après l'appui : le chiffre attend son mot, pas seulement la fin de la chute.
const APPARITION_3000 = FIN_CHUTE + 1200;

// La reconstruction : même ordre que la construction, plus vite, plus sûr.
const RELEVE_DEPART = 150;
const PAS_RELEVE = 85;
const DUREE_RELEVE = 560;
const FIN_RELEVE = RELEVE_DEPART + (CARTES.length - 1) * PAS_RELEVE + DUREE_RELEVE;

const transformDe = (c, p) =>
    `translate3d(${(p.x - c.w / 2).toFixed(3)}em, ${(p.y - c.h / 2).toFixed(3)}em, ${p.z.toFixed(3)}em) ` +
    `rotateZ(${p.rz.toFixed(2)}deg) rotateY(${p.ry.toFixed(2)}deg) rotateX(${p.rx.toFixed(2)}deg)`;

const ROUGE = (couleur) => couleur === '♥' || couleur === '♦';

/* L'ombre d'une face n'est pas un aplat : elle se creuse vers le pied de la
   carte, là où le chevron se referme et où la lumière n'arrive plus. */
const degradeOmbre = (ombre) =>
    `linear-gradient(180deg, rgba(11,6,22,${(ombre * 0.55).toFixed(2)}) 0%, rgba(11,6,22,${Math.min(0.85, ombre * 1.5).toFixed(2)}) 100%)`;

/* Le recto : blanc cassé, index aux deux coins, grand symbole au centre. */
const Recto = ({ face: [valeur, couleur], ombre }) => (
    <div
        className="absolute inset-0 rounded-[0.35em] overflow-hidden"
        style={{
            background: 'linear-gradient(160deg, #fbf8f1 0%, #f1ece0 100%)',
            boxShadow: 'inset 0 0 0 0.04em rgba(0,0,0,0.25), inset 0 0 0.6em rgba(120,100,60,0.18)',
            color: ROUGE(couleur) ? '#b3202a' : '#16161a',
            fontFamily: 'Georgia, "Times New Roman", serif',
            backfaceVisibility: 'hidden'
        }}
    >
        <div className="absolute leading-none text-center font-bold" style={{ top: '0.3em', left: '0.3em', fontSize: '1.05em' }}>
            {valeur}<br /><span style={{ fontSize: '0.85em' }}>{couleur}</span>
        </div>
        <div className="absolute leading-none text-center font-bold" style={{ bottom: '0.3em', right: '0.3em', fontSize: '1.05em', transform: 'rotate(180deg)' }}>
            {valeur}<br /><span style={{ fontSize: '0.85em' }}>{couleur}</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center" style={{ fontSize: valeur.length > 1 || /[JQK]/.test(valeur) ? '2.6em' : '3.2em' }}>
            {/[JQK]/.test(valeur) ? <span className="font-bold" style={{ fontSize: '0.8em' }}>{valeur}{couleur}</span> : couleur}
        </div>
        {/* Reflet satiné en haut, et la part d'ombre de la face. */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(200deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 35%)' }} />
        <div className="absolute inset-0" style={{ background: degradeOmbre(ombre) }} />
    </div>
);

/* Le verso : cadre blanc, trame losangée dans le violet du cas nº 3. */
const Verso = ({ ombre, retourne = true }) => (
    <div
        className="absolute inset-0 rounded-[0.35em] overflow-hidden"
        style={{
            background: '#f6f2ea',
            boxShadow: 'inset 0 0 0 0.04em rgba(0,0,0,0.3)',
            transform: retourne ? 'rotateY(180deg)' : undefined,
            backfaceVisibility: 'hidden'
        }}
    >
        <div
            className="absolute rounded-[0.2em]"
            style={{
                inset: '0.32em',
                background: `
                    radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.22) 0 18%, transparent 19%),
                    repeating-linear-gradient(45deg, rgba(255,255,255,0.16) 0 0.07em, transparent 0.07em 0.42em),
                    repeating-linear-gradient(-45deg, rgba(255,255,255,0.16) 0 0.07em, transparent 0.07em 0.42em),
                    linear-gradient(160deg, #6d28d9 0%, #4c1d95 55%, #3b0f78 100%)`,
                boxShadow: 'inset 0 0 0 0.05em rgba(255,255,255,0.55)'
            }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(200deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 40%)' }} />
        <div className="absolute inset-0" style={{ background: degradeOmbre(ombre) }} />
    </div>
);

/* Une carte. `etat` : 'en-l-air' → 'debout' (construction), 'a-terre'
   (effondrement), 'relevee' (reconstruction). La transition dépend de la pose
   VISÉE : chaque mouvement a sa courbe. */
const Carte = ({ carte, index, etat }) => {
    let pose = carte.debout;
    let transition;
    if (etat === 'en-l-air') {
        pose = carte.enLAir;
        transition = 'none';
    } else if (etat === 'debout') {
        const delai = DEPART + index * PAS_POSE;
        // Dépasse à peine (« back-out ») : le petit rebond d'une carte qu'on
        // lâche du bout des doigts.
        transition = `transform ${DUREE_POSE}ms cubic-bezier(0.34, 1.35, 0.64, 1) ${delai}ms, opacity 180ms ease-out ${delai}ms`;
    } else if (etat === 'a-terre') {
        pose = carte.aTerre;
        const { delai, duree } = chute(carte, index);
        // Accélère jusqu'au sol : ça ne glisse pas, ça tombe.
        transition = `transform ${Math.round(duree)}ms cubic-bezier(0.55, 0, 0.9, 0.45) ${Math.round(delai)}ms`;
    } else {
        const delai = RELEVE_DEPART + index * PAS_RELEVE;
        transition = `transform ${DUREE_RELEVE}ms cubic-bezier(0.34, 1.2, 0.64, 1) ${delai}ms`;
    }

    // Penchée : la face tournée vers la lumière reste claire, l'autre passe
    // dans l'ombre du chevron. À terre, les deux faces prennent le jour.
    const versLaLumiere = carte.angle > 0;
    const couchee = etat === 'a-terre';
    return (
        <div
            data-carte={carte.type}
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: `${carte.w}em`,
                height: `${carte.h}em`,
                transformStyle: 'preserve-3d',
                transform: transformDe(carte, pose),
                opacity: etat === 'en-l-air' ? 0 : 1,
                transition
            }}
        >
            {carte.type === 'plateau' ? (
                <>
                    <Verso ombre={0} retourne={false} />
                    <Verso ombre={0.55} />
                </>
            ) : (
                <>
                    <Recto face={carte.face} ombre={couchee ? 0.08 : versLaLumiere ? 0.04 : 0.3} />
                    <Verso ombre={couchee ? 0.1 : versLaLumiere ? 0.5 : 0.08} />
                </>
            )}
        </div>
    );
};

/**
 * Le château seul. `etape` : 0 construction · 1 effondré · 2 et plus relevé.
 * @param hauteur  hauteur réservée (CSS).
 */
const Chateau = ({ etape = 0, hauteur }) => {
    const [pose, setPose] = useState(false);
    const [tremble, setTremble] = useState(false);

    useEffect(() => {
        // Un court délai plutôt que requestAnimationFrame : l'état « en l'air »
        // doit être peint avant la transition, et rAF est suspendu dans un
        // onglet qui n'est pas au premier plan — les cartes ne tombaient jamais.
        const t0 = setTimeout(() => setPose(true), 40);
        const t = setTimeout(() => setTremble(true), FIN_POSE - 150);
        return () => {
            clearTimeout(t0);
            clearTimeout(t);
        };
    }, []);

    const etat = etape >= 2 ? 'relevee' : etape === 1 ? 'a-terre' : pose ? 'debout' : 'en-l-air';
    const scene = 'rotateX(-20deg) rotateY(-26deg)';

    return (
        <div
            className="relative flex items-end justify-center"
            data-chateau={etat}
            style={{
                height: hauteur,
                width: '100%',
                // 1 em ≈ 1/31 de la hauteur réservée : le château la remplit.
                fontSize: `calc(${hauteur} / 31)`
            }}
            aria-hidden="true"
        >
            {/* Halo d'ambiance, comme derrière le verre. */}
            <div className="absolute pointer-events-none rounded-full" style={{ width: '38em', height: '30em', bottom: '4em', background: 'radial-gradient(closest-side, rgba(139,92,246,0.22), transparent)', filter: 'blur(1em)' }} />

            {/* Le tapis, et l'ombre portée du château : elle se forme avec la
                construction et s'étale quand les cartes sont à terre. */}
            <div className="absolute pointer-events-none" style={{ bottom: '0.5em', width: '52em', height: '10em', borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(46,24,84,0.85), rgba(30,16,56,0.45) 60%, transparent)' }} />
            <div
                className="absolute pointer-events-none"
                style={{
                    bottom: '2.6em',
                    width: etat === 'a-terre' ? '40em' : '30em',
                    height: '4.2em',
                    borderRadius: '50%',
                    background: 'radial-gradient(closest-side, rgba(0,0,0,0.75), transparent)',
                    filter: 'blur(0.4em)',
                    opacity: etat === 'en-l-air' ? 0 : etat === 'a-terre' ? 0.55 : 1,
                    transition: etat === 'debout'
                        ? `opacity ${FIN_POSE - DEPART}ms ease-in ${DEPART}ms`
                        : 'opacity 900ms ease, width 900ms ease'
                }}
            />

            {/* La scène 3D. Vue de trois quarts, un peu plongeante. */}
            <div style={{ position: 'absolute', bottom: '4.6em', perspective: '90em', perspectiveOrigin: '50% 20%' }}>
                <div
                    className={tremble && etape === 0 ? 'chateau-tremble' : undefined}
                    style={{ position: 'relative', width: 0, height: 0, transformStyle: 'preserve-3d', transform: scene }}
                >
                    {CARTES.map((carte, i) => (
                        <Carte key={i} carte={carte} index={i} etat={etat} />
                    ))}
                </div>
            </div>

            <style>{`
                .chateau-tremble { animation: chateauTremble 700ms ease-in-out 1; }
                @keyframes chateauTremble {
                    0%, 100% { transform: ${scene} rotateZ(0deg); }
                    20% { transform: ${scene} rotateZ(0.7deg); }
                    45% { transform: ${scene} rotateZ(-0.5deg); }
                    70% { transform: ${scene} rotateZ(0.25deg); }
                }
            `}</style>
        </div>
    );
};

/* ─────────────── La slide ─────────────── */

const HAUTEUR_CHATEAU = 'min(44vh, 34vw)';
const TAILLE_CHIFFRE = 'clamp(2rem, min(5.2vw, 9vh), 5.5rem)';
const TAILLE_LABEL = 'clamp(0.75rem, min(1.4vw, 2.4vh), 1.4rem)';
const ECART = '2.5vw';

const Chiffre = ({ label, value, gradient, style, id }) => (
    <div data-chiffre={id} className="absolute top-0 left-1/2 flex flex-col items-center whitespace-nowrap" style={style}>
        <span className="text-slate-300/80 font-light" style={{ fontSize: TAILLE_LABEL }}>{label}</span>
        <span className={`font-black leading-none bg-gradient-to-r ${gradient} bg-clip-text text-transparent`} style={{ fontSize: TAILLE_CHIFFRE }}>
            {value}
        </span>
    </div>
);

/**
 * La slide 27 entière, rendue par StatementSlide quand `intro: "chateau"`.
 * Tout l'espace est RÉSERVÉ dès l'ouverture — bandeau des chiffres, château,
 * ligne de la note : rien ne bouge d'un appui à l'autre, les éléments ne font
 * qu'apparaître.
 *
 * Champs de slide : kicker, note, accent, et
 *   chateau: { avant: { label, value }, apres: { label, value } }
 * Le `title` n'est pas affiché ici ; il reste la légende de la slide pour le
 * prompteur.
 */
export const ForgeChateau = ({ slide, slideKey, etape = 0 }) => {
    const ton = accent(slide.accent);
    const { avant, apres } = slide.chateau || {};

    const avantVisible = etape >= 1;
    const recule = etape >= 2;
    // Les chiffres attendent le mouvement ET le mot : 3000 une fois les cartes
    // à terre, sur « plus de… » ; 1000 à la dernière carte relevée.
    const styleAvant = {
        opacity: !avantVisible ? 0 : recule ? 0.5 : 1,
        transform: recule
            ? `translateX(calc(-100% - ${ECART})) translateY(0em) scale(0.72)`
            : `translateX(-50%) translateY(${avantVisible ? '0em' : '0.6em'}) scale(1)`,
        transformOrigin: '100% 60%',
        transition: recule
            ? `opacity 700ms ease ${FIN_RELEVE - 250}ms, transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${FIN_RELEVE - 250}ms`
            : `opacity 500ms ease ${APPARITION_3000}ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) ${APPARITION_3000}ms`
    };
    const styleApres = {
        opacity: recule ? 1 : 0,
        transform: `translateX(${ECART}) translateY(${recule ? '0em' : '0.6em'}) scale(1)`,
        transition: `opacity 500ms ease ${FIN_RELEVE}ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) ${FIN_RELEVE}ms`
    };

    return (
        <div key={slideKey} className="h-full flex flex-col items-center justify-center px-16 pb-16 text-center relative overflow-hidden">
            <div className="relative z-10 w-full max-w-6xl flex flex-col items-center gap-[2vh]">
                {slide.kicker && (
                    <p
                        className={`font-mono uppercase tracking-[0.3em] opacity-70 animate-fade-in-up ${ton.texte}`}
                        style={{ fontSize: 'clamp(0.7rem, min(1.3vw, 2.4vh), 1.5rem)' }}
                    >
                        {slide.kicker}
                    </p>
                )}

                <div className="relative w-full" style={{ height: 'clamp(4.5rem, 14vh, 10rem)' }}>
                    {avant && <Chiffre id="avant" {...avant} gradient="from-red-400 via-orange-400 to-red-400" style={styleAvant} />}
                    {apres && <Chiffre id="apres" {...apres} gradient={ton.titre} style={styleApres} />}
                </div>

                <Chateau etape={etape} hauteur={HAUTEUR_CHATEAU} />

                {slide.note && (
                    <p
                        data-note
                        className="text-slate-300/70 font-light leading-snug"
                        style={{
                            fontSize: 'clamp(0.9rem, min(1.9vw, 3.4vh), 1.9rem)',
                            opacity: etape >= 3 ? 1 : 0,
                            transform: `translateY(${etape >= 3 ? '0em' : '0.5em'})`,
                            transition: 'opacity 600ms ease, transform 600ms ease'
                        }}
                    >
                        {slide.note}
                    </p>
                )}
            </div>
        </div>
    );
};

export default Chateau;
